/// <amd-module name="Feed/Legacy/Actions/FeedSettingsActionCreator" />
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IReduxStoreService } from "VSS/Redux/StoreService";
import { Action } from "VSS/Reflux/Action";

import { FeedSettingsCi, PerfScenarios } from "Feed/Common/Constants/Constants";
import { Feed as Feed_, FeedPermission, FeedView, UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub";
import { ChangeDefaultViewHandler } from "Feed/Legacy/Actions/FeedSettings/ChangeDefaultViewHandler";
import { ChangeSelectedPermissionsHandler } from "Feed/Legacy/Actions/FeedSettings/ChangeSelectedPermissionsHandler";
import { ChangeSelectedUpstreamSourcesHandler } from "Feed/Legacy/Actions/FeedSettings/ChangeSelectedUpstreamSourcesHandler";
import { ChangeSelectedViewsHandler } from "Feed/Legacy/Actions/FeedSettings/ChangeSelectedViewsHandler";
import { CurrentPivotHandler } from "Feed/Legacy/Actions/FeedSettings/CurrentPivotHandler";
import { DeleteFeedHandler } from "Feed/Legacy/Actions/FeedSettings/DeleteFeedHandler";
import { DeletePermissionsHandler } from "Feed/Legacy/Actions/FeedSettings/DeletePermissionsHandler";
import { DeleteViewsHandler } from "Feed/Legacy/Actions/FeedSettings/DeleteViewsHandler";
import { DisplayRetentionPolicyHandler } from "Feed/Legacy/Actions/FeedSettings/DisplayRetentionPolicyHandler";
import {
    ChangeBadgesEnabledHandler,
    ChangeFeedDescriptionHandler,
    ChangeFeedNameHandler,
    ChangeFeedRetentionHandler,
    ChangeHideDeletedPackageVersionsHandler
} from "Feed/Legacy/Actions/FeedSettings/FeedDetailsHandlers";
import { GetScopedBuildPermission } from "Feed/Legacy/Actions/FeedSettings/GetScopedBuildPermission";
import {
    ClearErrorHandler,
    DisplayErrorHandler,
    DisplayWarningHandler
} from "Feed/Legacy/Actions/FeedSettings/MessageBarHandlers";
import { NavigateToPermissionsHandler } from "Feed/Legacy/Actions/FeedSettings/NavigateToPermissionsHandler";
import { NavigateToViewsHandler } from "Feed/Legacy/Actions/FeedSettings/NavigateToViewsHandler";
import { PermanentDeleteFeedHandler } from "Feed/Legacy/Actions/FeedSettings/PermanentDeleteFeedHandler";
import { PermissionValidator } from "Feed/Legacy/Actions/FeedSettings/PermissionValidator";
import { RestoreFeedHandler } from "Feed/Legacy/Actions/FeedSettings/RestoreFeedHandler";
import { SaveFeedDetailsHandler } from "Feed/Legacy/Actions/FeedSettings/SaveFeedDetailsHandler";
import { SavePermissionsHandler } from "Feed/Legacy/Actions/FeedSettings/SavePermissionsHandler";
import { SaveUpstreamSourcesHandler } from "Feed/Legacy/Actions/FeedSettings/SaveUpstreamSourcesHandler";
import { SaveViewHandler } from "Feed/Legacy/Actions/FeedSettings/SaveViewHandler";
import {
    ToggleAddUsersOrGroupsPanelDisplayHandler
} from "Feed/Legacy/Actions/FeedSettings/ToggleAddUsersOrGroupsPanelDisplayHandler";
import { ToggleDiscardDialogDisplayHandler } from "Feed/Legacy/Actions/FeedSettings/ToggleDiscardDialogDisplayHandler";
import { ToggleViewPanelDisplayHandler } from "Feed/Legacy/Actions/FeedSettings/ToggleViewPanelDisplayHandler";
import { UndoFeedDetailsHandler } from "Feed/Legacy/Actions/FeedSettings/UndoFeedDetailsHandler";
import { UpgradeFeedHandler } from "Feed/Legacy/Actions/FeedSettings/UpgradeFeedHandler";
import {
    ChangeUpstreamSourceLocationHandler,
    ChangeUpstreamSourceNameHandler,
    CloseAddUpstreamPanelHandler,
    DisplayErrorHandlerAddUpstreamSources,
    OpenAddUpstreamPanelHandler
} from "Feed/Legacy/Actions/FeedSettings/UpstreamHandlers";
import { ValidationHandler } from "Feed/Legacy/Actions/FeedSettings/ValidationHandler";
import { ViewNameValidator } from "Feed/Legacy/Actions/FeedSettings/ViewNameValidator";
import { ViewPermissionValidator } from "Feed/Legacy/Actions/FeedSettings/ViewPermissionValidator";
import { SettingsActions } from "Feed/Legacy/Actions/SettingsActions";
import { IUpstreamHealthStatusDialogDetails } from "Feed/Legacy/Common/ActionPayloads";
import { NavigationHandler } from "Feed/Legacy/Common/NavigationHandler";
import { DependencyAction, IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { RoleHelper } from "Feed/Legacy/Helpers/RoleHelper";
import { FeedStore } from "Feed/Legacy/Stores/FeedStore";
import { FeedRetentionChanges } from "Feed/Legacy/Types/FeedRetentionChanges";
import { ExtendedUpstreamSource, Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class FeedSettingsActionCreator {
    /**
     * Message bar is dismissed by user, remove the message bar
     */
    public static onDismissMessageBar: Action<{}>;

    /**
     * Settings hub was loaded with this pivot, store it
     */
    public static onInitialPivotLoad: Action<string>;

    /**
     * User tried to switch pivots while having unsaved changes
     * So show discard changes dialog
     */
    public static showDiscardChangesDialog: Action<{}>;

    /**
     * User switched to a pivot, store it
     */
    public static onPivotChanged: Action<string>;

    /**
     * User wants to discard changes in current pivot
     */
    public static onDiscardChangesClicked: Action<{}>;

    /**
     * User wants to stay on current pivot
     */
    public static onStayOnCurrentPivotClicked: Action<{}>;

    /**
     * Show Delete Feed dialog when user clicked on Delete Feed button in PivotBar
     * Or Hide when Cancel button in dialog is clicked
     */
    public static showDeleteFeedDialog: Action<boolean>;

    /**
     * Show Permanent delete Feed dialog when user clicked on Permanent Delete Feed button in PivotBar
     * Or Hide when Cancel button in dialog is clicked
     */
    public static showPermanentDeleteFeedDialog: Action<boolean>;

    /**
     * Restore the feed and navigate to the feed page
     */
    public static onRestoreFeedClicked: Action<boolean>;

    /**
     * User changed the description of the feed
     */
    public static onFeedDescriptionChanged: Action<string>;

    /**
     * User changed the name of the feed
     */
    public static onFeedNameChanged: Action<string>;

    /**
     * User changed the retention policy value for the maximum number of versions per package
     */
    public static onFeedRetentionPolicyChanged: Action<FeedRetentionChanges>;

    /**
     * User toggled the hideDeletedPackageVersions checkbox
     */
    public static toggleHideDeletedPackageVersions: Action<boolean>;

    /**
     * User toggled the badgesEnabled checkbox
     */
    public static toggleBadgesEnabled: Action<boolean>;

    /**
     * When user navigates to feed settings permission
     *  1. set loading spinner
     *  2. get feed's permission from server
     *  3. filter out roles that aren't shown in UI
     */
    public static navigatingToPermissions: Action<{}>;

    /**
     * Adds following identities (based on scope passed via action arg) with Contributors role to grid,
     * if they're not already present:
     *  1. Project Collection Build Service (arg is true)
     *  2. Project Build Service (project) (arg is false)
     */
    public static addScopedBuildPermissionClicked: Action<boolean>;

    /**
     * If content of add user or group panel changes, reset validation error message
     */
    public static addUsersOrGroupsPanelContentChange: Action<FeedPermission[]>;

    /**
     * User clicked 'Save' button in 'Add users or groups' panel
     * save new users/groups to server and add to grid
     */
    public static savePermissionsClicked: Action<FeedPermission[]>;

    /**
     * Show Delete permissions dialog when user clicked on Delete button in Permissions pivot
     */
    public static showDeletePermissionsDialog: Action<{}>;

    /**
     * User selected permission indices in Permissions pivot
     */
    public static onPermissionSelectionChanged: Action<FeedPermission[]>;

    /**
     * User clicked Add User/Group button in PivotBarActions
     * Display Add User/Group dialog now
     */
    public static toggleAddUserOrGroupPanelDisplay: Action<boolean>;

    /**
     * When user navigates to feed settings permission
     *  1. set loading spinner
     *  2. get feed's views from server
     */
    public static navigatingToViews: Action<{}>;

    /**
     * User clicked Mark default view button in View's pivot command bar
     */
    public static markDefaultViewClicked: Action<FeedView>;

    /**
     * User selected view indices in Views pivot
     */
    public static onViewSelectionChanged: Action<FeedView[]>;

    /**
     * Show Delete views dialog when user clicked on Delete button in Views pivot
     */
    public static showDeleteViewsDialog: Action<{}>;

    /**
     * When user enters view name in Add view panel
     *  1. Validate the name
     *  2. Store current view name (even if validation fails)
     */
    public static onViewNameChanged: Action<{ viewName: string; originalViewName: string }>;

    /**
     * User clicked 'Add' button in 'Add view' panel, save view to server and add it to grid
     * User clicked 'Edit' button in 'Add/edit view' panel, save updates to server and grid
     */
    public static saveViewClicked: Action<{
        view: FeedView;
        makeThisDefaultView: boolean;
        viewPermissions: FeedPermission[];
    }>;

    /**
     * User selected a view permission to add on viewPanel, validate it and enable add view permission button
     */
    public static addViewPermissionChanged: Action<{ viewId: string; permission: FeedPermission }>;

    /**
     * User clicked Add view or Edit view button in PivotBarActions
     * Display view panel now
     */
    public static toggleViewPanelDisplay: Action<{ isOpen: boolean; isEditing: boolean }>;

    /**
     * User clicked Save on AddUpstreamPanel, add that upstream source
     */
    public static addUpstreamSources: Action<ExtendedUpstreamSource[]>;

    /**
     * User reordered the upstreams in the upstream settings list
     */
    public static reorderUpstreamSources: Action<{
        fromIndexOfUpstreamSource: UpstreamSource;
        toIndexOfUpstreamSource: UpstreamSource;
    }>;

    /**
     * Upstream sources selected in grid
     */
    public static onUpstreamSourcesSelectionChanged: Action<UpstreamSource[]>;

    /**
     * Show Delete upstream sources dialog when user clicked on Delete button in Upstream sources pivot
     */
    public static showDeleteUpstreamSourcesDialog: Action<{}>;

    /**
     * User modified the upstream source's name in AddUpstreamPanel
     */
    public static changeUpstreamSourceName: Action<string>;

    /**
     * User modified the upstream source's location in AddUpstreamPanel
     */
    public static changeUpstreamSourceLocation: Action<string>;

    /**
     * User clicked on add public upstream source
     */
    public static openAddUpstreamPanelRequested: Action<{}>;

    /**
     * User requested to close the AddUpstreamPanel
     */
    public static closeAddUpstreamPanelRequested: Action<{}>;

    /**
     * Opens upstream health status dialog
     */
    public static openUpstreamStatusDialog: Action<IUpstreamHealthStatusDialogDetails>;

    /**
     * Undo changes made to feed details pivot
     */
    public static undoFeedDetailsChanges: Action<{}>;

    /**
     * Feed details save changes made to server
     */
    public static saveFeedDetailsChanges: Action<{}>;

    /**
     * Feed details save changes made to server
     */
    public static upgradeFeed: Action<{}>;

    /**
     * @param store - when actions makes state change, this store will be updated
     */
    constructor(store: FeedStore) {
        this._store = store;
        this._initialize();
    }

    /**
     * Initialize all actions relevant to a state property
     */
    private _initialize(): void {
        const emitCallback = (): void => {
            this.emit();
        };
        const emptyCallback = (): void => {
            return;
        };

        FeedSettingsActionCreator.onInitialPivotLoad = new Action<string>();
        FeedSettingsActionCreator.onInitialPivotLoad.addListener((initialPivotKey: string) => {
            const state = this.getState();
            CurrentPivotHandler.handle(state, emptyCallback, initialPivotKey);
            if (state.feed().deletedDate == null) {
                DisplayRetentionPolicyHandler.handleAsync(state, emitCallback);
            }
            if (!state.isUserAdmin()) {
                DisplayWarningHandler.handle(state, emitCallback, PackageResources.FeedSettings_UserNotAdmin_Warning);
            }
        });

        FeedSettingsActionCreator.showDiscardChangesDialog = new Action();
        FeedSettingsActionCreator.showDiscardChangesDialog.addListener(() => {
            const state = this.getState();
            ToggleDiscardDialogDisplayHandler.handle(state, emitCallback, true /* show */);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DiscardDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.onPivotChanged = new Action<string>();
        FeedSettingsActionCreator.onPivotChanged.addListener((newPivotKey: string): void => {
            const state = this.getState();
            CurrentPivotHandler.handle(state, emitCallback, newPivotKey);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.PivotChanged, {
                feedId: state.feed().id,
                pivot: newPivotKey
            });
        });

        FeedSettingsActionCreator.onDiscardChangesClicked = new Action();
        FeedSettingsActionCreator.onDiscardChangesClicked.addListener(() => {
            const state = this.getState();
            UndoFeedDetailsHandler.handle(state, emitCallback);
            ToggleDiscardDialogDisplayHandler.handle(state, emitCallback, false /* hide */);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DiscardDialog, {
                feedId: state.feed().id,
                action: "discardchanges"
            });
        });

        FeedSettingsActionCreator.onStayOnCurrentPivotClicked = new Action();
        FeedSettingsActionCreator.onStayOnCurrentPivotClicked.addListener(() => {
            const state = this.getState();
            ToggleDiscardDialogDisplayHandler.handle(state, emitCallback, false /* hide */);
            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DiscardDialog, {
                feedId: state.feed().id,
                action: "stayOnCurrentPivot"
            });
        });

        FeedSettingsActionCreator.onDismissMessageBar = new Action();
        FeedSettingsActionCreator.onDismissMessageBar.addListener(() => {
            const state = this.getState();
            DisplayErrorHandler.handle(state, emitCallback);
        });

        FeedSettingsActionCreator.showDeleteFeedDialog = new Action<boolean>();
        FeedSettingsActionCreator.showDeleteFeedDialog.addListener((show: boolean) => {
            const state = this.getState();
            const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            const onSaveCallback = async () => {
                try {
                    await DeleteFeedHandler.handleAsync(state);
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DeleteFeed, {
                        feedId: state.feed().id
                    });

                    state.updateActionDependencies(DependencyAction.DeleteFeed, state.feed());
                } catch (error) {
                    DisplayErrorHandler.handle(
                        state,
                        () => {
                            return;
                        },
                        error
                    );
                }
            };
            const onDismiss = () => {
                reduxStore.dispatch(FeedHubActions.dismissDialog());
                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeleteFeedDialog, {
                    feedId: state.feed().id,
                    action: "dismissed"
                });
            };
            const deleteHeaderText = Utils_String.format(
                PackageResources.FeedDeletionDialogContent_Title,
                state.feed().name
            );
            reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: deleteHeaderText,
                    dialogBody: PackageResources.FeedDeletionDialogContent_DeleteFeedText,
                    className: "delete-dialog",
                    warningBody: PackageResources.FeedDeletionDialogContent_SevereWarningText,
                    primaryButtonText: PackageResources.PackageCommands_DeletePackage,
                    primaryButtonClassName: "delete-button",
                    primaryButtonDanger: true,
                    primaryButtonLoadingText: PackageResources.PackageCommands_DeletePackage_Deleting,
                    primaryButtonCallback: async () => {
                        CustomerIntelligenceHelper.publishEvent(
                            state.pageContext,
                            FeedSettingsCi.ShowDeleteFeedDialog,
                            {
                                feedId: state.feed().id,
                                action: "deleting"
                            }
                        );

                        try {
                            await onSaveCallback();
                        } finally {
                            onDismiss();
                            emitCallback();
                        }
                    },
                    onDismissCallback: onDismiss
                })
            );
            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeleteFeedDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.showPermanentDeleteFeedDialog = new Action<boolean>();
        FeedSettingsActionCreator.showPermanentDeleteFeedDialog.addListener((show: boolean) => {
            const state = this.getState();
            const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            const onSaveCallback = async () => {
                try {
                    await PermanentDeleteFeedHandler.handleAsync(state);
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.PermanentDeleteFeed, {
                        feedId: state.feed().id
                    });

                    state.updateActionDependencies(DependencyAction.PermanentlyDeleteFeed, state.feed());
                } catch (error) {
                    DisplayErrorHandler.handle(state, emptyCallback, error);
                }
            };
            const onDismiss = () => {
                reduxStore.dispatch(FeedHubActions.dismissDialog());
                CustomerIntelligenceHelper.publishEvent(
                    state.pageContext,
                    FeedSettingsCi.ShowPermanentDeleteFeedDialog,
                    {
                        feedId: state.feed().id,
                        action: "dismissed"
                    }
                );
            };
            reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: PackageResources.FeedPermanentDeletionDialogContent_Title,
                    dialogBody: PackageResources.FeedPermanentDeletionDialogContent_PermanentDeleteFeedText,
                    className: "delete-dialog",
                    warningBody: PackageResources.FeedPermanentDeletionDialogContent_SevereWarningText,
                    primaryButtonText: PackageResources.PackageCommands_DeletePackage,
                    primaryButtonClassName: "delete-button",
                    primaryButtonDanger: true,
                    primaryButtonLoadingText: PackageResources.PackageCommands_DeletePackage_Deleting,
                    primaryButtonCallback: async () => {
                        CustomerIntelligenceHelper.publishEvent(
                            state.pageContext,
                            FeedSettingsCi.ShowPermanentDeleteFeedDialog,
                            {
                                feedId: state.feed().id,
                                action: "permanent deleting"
                            }
                        );

                        try {
                            await onSaveCallback();
                        } finally {
                            onDismiss();
                            emitCallback();
                        }
                    },
                    onDismissCallback: onDismiss
                })
            );
            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowPermanentDeleteFeedDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.onRestoreFeedClicked = new Action<boolean>();
        FeedSettingsActionCreator.onRestoreFeedClicked.addListener(async (show: boolean) => {
            const state = this.getState();
            state.isSavingChanges = true;
            this.emit();
            try {
                await RestoreFeedHandler.handleAsync(state);
                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.RestoreFeed, {
                    feedId: state.feed().id
                });

                state.updateActionDependencies(DependencyAction.RestoreFeed, state.feed());
            } catch (error) {
                DisplayErrorHandler.handle(state, emptyCallback, error);
            } finally {
                state.isSavingChanges = false;
                this.emit();
            }
        });

        FeedSettingsActionCreator.onFeedDescriptionChanged = new Action<string>();
        FeedSettingsActionCreator.onFeedDescriptionChanged.addListener((description: string) => {
            const state = this.getState();
            ChangeFeedDescriptionHandler.handle(state, emitCallback, description);
        });

        FeedSettingsActionCreator.onFeedNameChanged = new Action<string>();
        FeedSettingsActionCreator.onFeedNameChanged.addListener((name: string) => {
            // always get latest state
            const state = this.getState();
            const validation = ChangeFeedNameHandler.handle(state, emitCallback, name);
            ValidationHandler.handle(state, emitCallback, [validation]);
        });

        FeedSettingsActionCreator.onFeedRetentionPolicyChanged = new Action<FeedRetentionChanges>();
        FeedSettingsActionCreator.onFeedRetentionPolicyChanged.addListener((changes: FeedRetentionChanges) => {
            // always get latest state
            const state = this.getState();

            // Have 2 fields to update, so need to update both at the same time.
            const validation = ChangeFeedRetentionHandler.handle(state, emitCallback, changes);
            ValidationHandler.handle(state, emitCallback, validation);
        });

        FeedSettingsActionCreator.toggleHideDeletedPackageVersions = new Action<boolean>();
        FeedSettingsActionCreator.toggleHideDeletedPackageVersions.addListener(
            (hideDeletedPackageVersions: boolean) => {
                // always get latest state
                const state = this.getState();
                ChangeHideDeletedPackageVersionsHandler.handle(state, emitCallback, hideDeletedPackageVersions);
            }
        );

        FeedSettingsActionCreator.saveFeedDetailsChanges = new Action();
        FeedSettingsActionCreator.saveFeedDetailsChanges.addListener(async () => {
            const state = this.getState();
            state.isSavingChanges = true;
            this.emit();
            try {
                const updatedFeed: Feed_ = await SaveFeedDetailsHandler.handleAsync(state);
                if (updatedFeed != null) {
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.SaveFeedDetails, {
                        feedId: updatedFeed.id,
                        hideDeletedPackageVersions: updatedFeed.hideDeletedPackageVersions,
                        badgesEnabled: updatedFeed.badgesEnabled,
                        retentionPolicy: state.retentionPolicySettings.retentionPolicy
                    });

                    // Handled by FeedStore, to update the feed
                    SettingsActions.FeedUpdated.invoke(updatedFeed as Feed);
                    // Handled by PackageStore to update the retention policy
                    SettingsActions.FeedRetentionPolicyUpdated.invoke({
                        feedId: updatedFeed.id,
                        retentionPolicy: state.retentionPolicySettings.retentionPolicy
                    });
                    NavigationHandler.updateFeedNameInUrlIfChanged(state.pageContext, updatedFeed as Feed);
                }
                // if Save was invoked from 'Unsaved changes' dialog
                ToggleDiscardDialogDisplayHandler.handle(state, emptyCallback, false /* hide */);
                ClearErrorHandler.handle(state, emptyCallback);
            } catch (error) {
                DisplayErrorHandler.handle(state, emptyCallback, error);
            } finally {
                state.isSavingChanges = false;
                this.emit();
            }
        });

        FeedSettingsActionCreator.undoFeedDetailsChanges = new Action();
        FeedSettingsActionCreator.undoFeedDetailsChanges.addListener(() => {
            // always get latest state
            const state = this.getState();
            UndoFeedDetailsHandler.handle(state, emptyCallback);
            ClearErrorHandler.handle(state, emitCallback);
        });

        FeedSettingsActionCreator.toggleBadgesEnabled = new Action<boolean>();
        FeedSettingsActionCreator.toggleBadgesEnabled.addListener((badgesEnabled: boolean) => {
            // always get latest state
            const state = this.getState();
            ChangeBadgesEnabledHandler.handle(state, emitCallback, badgesEnabled);
        });

        FeedSettingsActionCreator.navigatingToPermissions = new Action();
        FeedSettingsActionCreator.navigatingToPermissions.addListener(() => {
            // always get latest state
            const state = this.getState();
            NavigateToPermissionsHandler.handleAsync(state, emitCallback).catch((error: Error) => {
                DisplayErrorHandler.handle(state, emitCallback, error);
            });
        });

        FeedSettingsActionCreator.addScopedBuildPermissionClicked = new Action();
        FeedSettingsActionCreator.addScopedBuildPermissionClicked.addListener(async (isCollectionScope: boolean) => {
            // always get latest state
            const state = this.getState();

            const permission: FeedPermission = GetScopedBuildPermission(state.pageContext, isCollectionScope);
            await this.savePermissionsAsync([permission]);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddScopedBuildPermission, {
                feedId: state.feed().id,
                scope: isCollectionScope ? "collection" : "project"
            });
        });

        FeedSettingsActionCreator.addUsersOrGroupsPanelContentChange = new Action<FeedPermission[]>();
        FeedSettingsActionCreator.addUsersOrGroupsPanelContentChange.addListener((permissions: FeedPermission[]) => {
            const state = this.getState();
            const validationResult = PermissionValidator.GetValidationResult(state, permissions);
            ValidationHandler.handle(state, emitCallback, [validationResult]);
        });

        FeedSettingsActionCreator.savePermissionsClicked = new Action<FeedPermission[]>();
        FeedSettingsActionCreator.savePermissionsClicked.addListener(async (permissions: FeedPermission[]) => {
            await this.savePermissionsAsync(permissions);
        });

        FeedSettingsActionCreator.showDeletePermissionsDialog = new Action<{}>();
        FeedSettingsActionCreator.showDeletePermissionsDialog.addListener(() => {
            const state = this.getState();
            const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            const onSaveCallback = async () => {
                try {
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DeletePermissions, {
                        feedId: state.feed().id,
                        count: state.selectedPermissions.length
                    });

                    await DeletePermissionsHandler.handleAsync(state);
                } catch (error) {
                    DisplayErrorHandler.handle(state, emptyCallback, error);
                }
            };
            const onDismiss = () => {
                reduxStore.dispatch(FeedHubActions.dismissDialog());
                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeletePermissionsDialog, {
                    feedId: state.feed().id,
                    action: "dismissed"
                });
            };
            reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: PackageResources.GeneralDialog_PermissionDelete_Header,
                    dialogBody: PackageResources.GeneralDialog_PermissionDelete_Content,
                    className: "delete-dialog",
                    primaryButtonText: PackageResources.PackageCommands_DeletePackage,
                    primaryButtonClassName: "delete-button",
                    primaryButtonDanger: true,
                    primaryButtonLoadingText: PackageResources.PackageCommands_DeletePackage_Deleting,
                    primaryButtonCallback: async () => {
                        CustomerIntelligenceHelper.publishEvent(
                            state.pageContext,
                            FeedSettingsCi.ShowDeletePermissionsDialog,
                            {
                                feedId: state.feed().id,
                                action: "deleting"
                            }
                        );

                        try {
                            await onSaveCallback();
                        } finally {
                            onDismiss();
                            emitCallback();
                        }
                    },
                    onDismissCallback: onDismiss
                })
            );

            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeletePermissionsDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.toggleAddUserOrGroupPanelDisplay = new Action<boolean>();
        FeedSettingsActionCreator.toggleAddUserOrGroupPanelDisplay.addListener((open: boolean) => {
            // always get latest state
            const state = this.getState();
            // reset validation error
            ValidationHandler.handle(state, emptyCallback, null);
            ToggleAddUsersOrGroupsPanelDisplayHandler.handle(state, emitCallback, open);

            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddUsersOrGroupsPanel, {
                feedId: state.feed().id,
                isOpen: open
            });
        });

        FeedSettingsActionCreator.navigatingToViews = new Action<{}>();
        FeedSettingsActionCreator.navigatingToViews.addListener(() => {
            // always get latest state
            const state = this.getState();
            NavigateToViewsHandler.handleAsync(state, emitCallback).catch((error: Error) => {
                DisplayErrorHandler.handle(state, emitCallback, error);
            });
        });

        FeedSettingsActionCreator.onViewNameChanged = new Action<{ viewName: string; originalViewName: string }>();
        FeedSettingsActionCreator.onViewNameChanged.addListener(({ viewName, originalViewName }) => {
            const state = this.getState();
            const validationResult = ViewNameValidator.GetValidationResult(state, viewName, originalViewName);
            ValidationHandler.handle(state, emitCallback, [validationResult]);
        });

        FeedSettingsActionCreator.markDefaultViewClicked = new Action<FeedView>();
        FeedSettingsActionCreator.markDefaultViewClicked.addListener(async (selectedView: FeedView) => {
            const state = this.getState();
            state.isSavingChanges = true;
            this.emit();
            try {
                await ChangeDefaultViewHandler.handleAsync(state, selectedView);

                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.SetAsDefaultView, {
                    feedId: state.feed().id,
                    viewId: selectedView.id,
                    viewType: selectedView.type,
                    viewVisibility: selectedView.visibility
                });
            } catch (error) {
                DisplayErrorHandler.handle(state, emptyCallback, error);
            } finally {
                state.isSavingChanges = false;
                this.emit();
            }
        });

        FeedSettingsActionCreator.saveViewClicked = new Action<{
            view: FeedView;
            makeThisDefaultView: boolean;
            viewPermissions: FeedPermission[];
        }>();
        FeedSettingsActionCreator.saveViewClicked.addListener(
            async ({ view, makeThisDefaultView, viewPermissions }) => {
                const state = this.getState();
                state.isSavingChanges = true;
                this.emit();
                try {
                    const feedView = await SaveViewHandler.handleAsync(state, view, viewPermissions);
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddViews, {
                        feedId: state.feed().id,
                        type: view.type,
                        visibility: view.visibility,
                        defaultView: makeThisDefaultView
                    });
                    if (makeThisDefaultView) {
                        await ChangeDefaultViewHandler.handleAsync(state, feedView);
                    }
                    ToggleViewPanelDisplayHandler.handle(state, emptyCallback, false /* isOpen */, null);
                    state.updateActionDependencies(DependencyAction.UpdateViews, state.views);
                } catch (error) {
                    state.error = error;
                } finally {
                    state.isSavingChanges = false;
                    state.selectedViews = [];
                    this.emit();
                }
            }
        );

        FeedSettingsActionCreator.showDeleteViewsDialog = new Action<{}>();
        FeedSettingsActionCreator.showDeleteViewsDialog.addListener(() => {
            const state = this.getState();
            const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            const onSaveCallback = async () => {
                try {
                    await DeleteViewsHandler.handleAsync(state);
                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DeleteViews, {
                        feedId: state.feed().id,
                        count: state.selectedViews.length
                    });
                } catch (error) {
                    DisplayErrorHandler.handle(state, emptyCallback, error);
                }
            };
            const onDismiss = () => {
                reduxStore.dispatch(FeedHubActions.dismissDialog());
                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeleteViewsDialog, {
                    feedId: state.feed().id,
                    action: "dismissed"
                });
            };
            reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: PackageResources.GeneralDialog_DeleteViews_Header,
                    dialogBody: PackageResources.GeneralDialog_DeleteViews_Content,
                    className: "delete-dialog",
                    warningBody: PackageResources.GeneralDialog_DeleteViews_Warning,
                    primaryButtonText: PackageResources.PackageCommands_DeletePackage,
                    primaryButtonClassName: "delete-button",
                    primaryButtonDanger: true,
                    primaryButtonLoadingText: PackageResources.PackageCommands_DeletePackage_Deleting,
                    primaryButtonCallback: async () => {
                        CustomerIntelligenceHelper.publishEvent(
                            state.pageContext,
                            FeedSettingsCi.ShowDeleteViewsDialog,
                            {
                                feedId: state.feed().id,
                                action: "deleting"
                            }
                        );

                        try {
                            await onSaveCallback();
                        } finally {
                            onDismiss();
                            emitCallback();
                        }
                    },
                    onDismissCallback: onDismiss
                })
            );
            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeleteViewsDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.addViewPermissionChanged = new Action<{
            viewId: string;
            permission: FeedPermission;
        }>();
        FeedSettingsActionCreator.addViewPermissionChanged.addListener(({ viewId, permission }) => {
            const state = this.getState();
            const validationResult = ViewPermissionValidator.GetValidationResult(state, viewId, permission);
            ValidationHandler.handle(state, emitCallback, [validationResult]);
        });

        FeedSettingsActionCreator.toggleViewPanelDisplay = new Action<{ isOpen: boolean; isEditing: boolean }>();
        FeedSettingsActionCreator.toggleViewPanelDisplay.addListener(({ isOpen, isEditing }) => {
            const state = this.getState();
            // reset validation state
            ValidationHandler.handle(state, emptyCallback, null);
            ToggleViewPanelDisplayHandler.handle(state, emitCallback, isOpen, isEditing);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddOrEditViewPanel, {
                feedId: state.feed().id,
                isOpen,
                isEditing
            });
        });

        FeedSettingsActionCreator.addUpstreamSources = new Action<ExtendedUpstreamSource[]>();
        FeedSettingsActionCreator.addUpstreamSources.addListener(async (upstreamSources: ExtendedUpstreamSource[]) => {
            // always get latest state
            const state = this.getState();

            const customPublicCount = upstreamSources.filter(s => s.isCustom === true).length;
            const performanceService = state.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            state.isSavingChanges = true;
            state.error = null;
            this.emit();
            try {
                // Set up performance telemetry event for custom upstreams if needed
                if (customPublicCount > 0) {
                    performanceService.startScenario(PerfScenarios.CustomPublicUpstreamSourceAdded, false);
                }

                await SaveUpstreamSourcesHandler.addAsync(state, upstreamSources);

                CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddUpstreamSources, {
                    feedId: state.feed().id,
                    count: upstreamSources.length,
                    internalCount: upstreamSources.filter(s => s.upstreamSourceType === UpstreamSourceType.Internal)
                        .length,
                    publicCount: upstreamSources.filter(s => s.upstreamSourceType === UpstreamSourceType.Public).length,
                    customPublicCount
                });

                CloseAddUpstreamPanelHandler.handle(state, emptyCallback);
            } catch (error) {
                if (customPublicCount > 0) {
                    // display error differently if it occured when adding a custom upstream source in addUpstreamsPanelSection
                    DisplayErrorHandlerAddUpstreamSources.handle(state, emptyCallback, error);
                } else {
                    state.error = error;
                }
            } finally {
                performanceService.endScenario(
                    PerfScenarios.Area,
                    PerfScenarios.CustomPublicUpstreamSourceAdded,
                    false
                );

                state.isSavingChanges = false;
                this.emit();
            }
        });

        FeedSettingsActionCreator.showDeleteUpstreamSourcesDialog = new Action<{}>();
        FeedSettingsActionCreator.showDeleteUpstreamSourcesDialog.addListener(() => {
            const state = this.getState();
            const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            const onSaveCallback = async () => {
                try {
                    await SaveUpstreamSourcesHandler.deleteAsync(state);

                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.DeleteUpstreamSources, {
                        feedId: state.feed().id,
                        count: state.selectedUpstreamSources.length
                    });
                } catch (error) {
                    DisplayErrorHandler.handle(state, emptyCallback, error);
                }
            };
            const onDismiss = () => {
                reduxStore.dispatch(FeedHubActions.dismissDialog());
                CustomerIntelligenceHelper.publishEvent(
                    state.pageContext,
                    FeedSettingsCi.ShowDeleteUpstreamSourcesDialog,
                    {
                        feedId: state.feed().id,
                        action: "dismissed"
                    }
                );
            };
            reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: PackageResources.GeneralDialog_DeleteUpstreamSources_Header,
                    dialogBody: PackageResources.GeneralDialog_DeleteUpstreamSources_Content,
                    className: "delete-dialog",
                    primaryButtonText: PackageResources.PackageCommands_DeletePackage,
                    primaryButtonClassName: "delete-button",
                    primaryButtonDanger: true,
                    primaryButtonLoadingText: PackageResources.PackageCommands_DeletePackage_Deleting,
                    primaryButtonCallback: async () => {
                        CustomerIntelligenceHelper.publishEvent(
                            state.pageContext,
                            FeedSettingsCi.ShowDeleteUpstreamSourcesDialog,
                            {
                                feedId: state.feed().id,
                                action: "deleting"
                            }
                        );

                        try {
                            await onSaveCallback();
                        } finally {
                            onDismiss();
                            emitCallback();
                        }
                    },
                    onDismissCallback: onDismiss
                })
            );
            emitCallback();
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ShowDeleteUpstreamSourcesDialog, {
                feedId: state.feed().id,
                action: "show"
            });
        });

        FeedSettingsActionCreator.openUpstreamStatusDialog = new Action<IUpstreamHealthStatusDialogDetails>();
        FeedSettingsActionCreator.openUpstreamStatusDialog.addListener(
            (dialogData: IUpstreamHealthStatusDialogDetails) => {
                const state = this.getState();
                const storeService = state.pageContext.getService<IReduxStoreService>("redux-store-service");
                const reduxStore = storeService.getStore(null);
                const onDismiss = () => {
                    reduxStore.dispatch(FeedHubActions.dismissDialog());
                };
                reduxStore.dispatch(
                    FeedHubActions.createDialog({
                        headerText: dialogData.headerText,
                        dialogBody: dialogData.dialogBody,
                        className: "upstream-status-dialog",
                        onDismissCallback: onDismiss,
                        warningBody: dialogData.warningBody,
                        dismissButtonText: PackageResources.Dialog_CloseButtonText
                    })
                );
                emitCallback();
                CustomerIntelligenceHelper.publishEvent(
                    state.pageContext,
                    FeedSettingsCi.ShowUpstreamHealthStatusDialog,
                    {
                        feedId: state.feed().id,
                        upstreamId: dialogData.upstreamId,
                        action: "show"
                    }
                );
            }
        );

        FeedSettingsActionCreator.reorderUpstreamSources = new Action<{
            fromIndexOfUpstreamSource: UpstreamSource;
            toIndexOfUpstreamSource: UpstreamSource;
        }>();
        FeedSettingsActionCreator.reorderUpstreamSources.addListener(
            async (data: { fromIndexOfUpstreamSource: UpstreamSource; toIndexOfUpstreamSource: UpstreamSource }) => {
                // always get latest state
                const state = this.getState();
                state.isSavingChanges = true;
                this.emit();
                try {
                    await SaveUpstreamSourcesHandler.reorderSourcesAsync(
                        state,
                        data.fromIndexOfUpstreamSource,
                        data.toIndexOfUpstreamSource
                    );

                    CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.ReorderUpstreamSources, {
                        feedId: state.feed().id,
                        fromUpstreamSourceType: data.fromIndexOfUpstreamSource.upstreamSourceType,
                        fromProtocol: data.fromIndexOfUpstreamSource.protocol,
                        toUpstreamSourceType: data.toIndexOfUpstreamSource.upstreamSourceType,
                        toProtocol: data.toIndexOfUpstreamSource.protocol
                    });
                } catch (error) {
                    DisplayErrorHandler.handle(state, emptyCallback, error);
                } finally {
                    state.isSavingChanges = false;
                    this.emit();
                }
            }
        );

        FeedSettingsActionCreator.changeUpstreamSourceName = new Action<string>();
        FeedSettingsActionCreator.changeUpstreamSourceName.addListener((upstreamSourceName: string) => {
            // always get latest state
            const state = this.getState();
            const validation = ChangeUpstreamSourceNameHandler.handle(state, upstreamSourceName);
            ValidationHandler.handle(state, emitCallback, [validation]);
        });

        FeedSettingsActionCreator.changeUpstreamSourceLocation = new Action<string>();
        FeedSettingsActionCreator.changeUpstreamSourceLocation.addListener((upstreamSourceLocation: string) => {
            // always get latest state
            const state = this.getState();
            const validation = ChangeUpstreamSourceLocationHandler.handle(state, upstreamSourceLocation);
            ValidationHandler.handle(state, emitCallback, [validation]);
        });

        FeedSettingsActionCreator.openAddUpstreamPanelRequested = new Action<{}>();
        FeedSettingsActionCreator.openAddUpstreamPanelRequested.addListener(() => {
            // always get latest state
            const state = this.getState();
            OpenAddUpstreamPanelHandler.handle(state, emitCallback);

            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddUpstreamSourcesPanel, {
                isOpen: true
            });
        });

        FeedSettingsActionCreator.closeAddUpstreamPanelRequested = new Action<{}>();
        FeedSettingsActionCreator.closeAddUpstreamPanelRequested.addListener(() => {
            // always get latest state
            const state = this.getState();
            // clear validation error
            ValidationHandler.handle(state, emptyCallback, null);
            CloseAddUpstreamPanelHandler.handle(state, emitCallback);

            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddUpstreamSourcesPanel, {
                isOpen: false
            });
        });

        FeedSettingsActionCreator.onPermissionSelectionChanged = new Action<FeedPermission[]>();
        FeedSettingsActionCreator.onPermissionSelectionChanged.addListener((selectedPermissions: FeedPermission[]) => {
            // always get latest state
            const state = this.getState();
            ChangeSelectedPermissionsHandler.handle(state, selectedPermissions);
            this.emit();
        });

        FeedSettingsActionCreator.onViewSelectionChanged = new Action<FeedView[]>();
        FeedSettingsActionCreator.onViewSelectionChanged.addListener((selectedViews: FeedView[]) => {
            // always get latest state
            const state = this.getState();
            ChangeSelectedViewsHandler.handle(state, selectedViews);
            this.emit();
        });

        FeedSettingsActionCreator.onUpstreamSourcesSelectionChanged = new Action<UpstreamSource[]>();
        FeedSettingsActionCreator.onUpstreamSourcesSelectionChanged.addListener(
            (selectedUpstreamSources: UpstreamSource[]) => {
                // always get latest state
                const state = this.getState();
                ChangeSelectedUpstreamSourcesHandler.handle(state, selectedUpstreamSources);
                this.emit();
            }
        );

        FeedSettingsActionCreator.upgradeFeed = new Action();
        FeedSettingsActionCreator.upgradeFeed.addListener(async () => {
            const state = this.getState();
            state.isSavingChanges = true;
            state.error = null;
            this.emit();
            try {
                const updatedFeed: Feed_ = await UpgradeFeedHandler.handleAsync(state);
                SettingsActions.FeedUpdated.invoke(updatedFeed as Feed);
                ClearErrorHandler.handle(state, emptyCallback);
                announce(PackageResources.FeedDetailsPane_UpgradeInProgress);
            } catch (error) {
                DisplayErrorHandler.handle(state, emptyCallback, error);
            } finally {
                state.isSavingChanges = false;
                this.emit();
            }
        });
    }

    // Note: Previously all handlers were passed a callback (emit or empty) as second parameter
    // to keep handler parameters consistent, now that we have enough handlers
    // using interface (IEmitChange, IDontEmitChange) will be a good idea?
    private async savePermissionsAsync(permissions: FeedPermission[]): Promise<void> {
        // always get latest state
        const state = this.getState();
        state.isSavingChanges = true;
        state.error = null;
        this.emit();
        try {
            await SavePermissionsHandler.handleAsync(state, permissions);
            CustomerIntelligenceHelper.publishEvent(state.pageContext, FeedSettingsCi.AddPermissions, {
                feedId: state.feed().id,
                count: permissions.length,
                role: RoleHelper.roleToLocaleString(permissions[0].role)
            });

            ToggleAddUsersOrGroupsPanelDisplayHandler.handle(
                state,
                () => {
                    return;
                },
                false /* close dialog */
            );
        } catch (error) {
            state.error = error;
        } finally {
            state.isSavingChanges = false;
            this.emit();
        }
    }

    /**
     * Calling this will propagate state changes to ControllerView tied to this store
     */
    private emit(): void {
        this._store.feedStoreEmitChanged();
    }

    /**
     * Get state specific to this Action handler
     */
    private getState(): IFeedSettingsState {
        return this._store.getFeedState().feedSettings;
    }

    private _store: FeedStore;
}
